/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { Component, Input, ViewEncapsulation } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { CommonModule, DatePipe } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ThaiDatePipe } from '@alfresco/aca-shared';
import { ExpiryService, ExpiryStatus } from './expiry.service';

/**
 * Document-list cell that flags a document's expiry status. Only warns (badge for
 * "expired" / "expiring soon"); shows nothing for documents without an expiry date
 * or whose expiry is still far off, to keep the list uncluttered.
 * Registered as `app.columns.expiry-status`; the cell receives the row via `context`.
 */
@Component({
  selector: 'app-expiry-status-column',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatTooltipModule, TranslatePipe, ThaiDatePipe],
  providers: [DatePipe],
  encapsulation: ViewEncapsulation.None,
  template: `
    @if (status === 'expired' || status === 'soon') {
      <span
        class="aca-expiry-badge aca-expiry-badge--{{ status }}"
        [matTooltip]="expiryDate ? ('DMS.UI.B94B8A' | translate) + ' ' + (expiryDate | acaDate: 'd MMM y') : ''"
      >
        <mat-icon>{{ status === 'expired' ? 'error' : 'warning' }}</mat-icon>
        {{ status === 'expired' ? ('DMS.UI.DBCA12' | translate) : ('DMS.UI.CA7C46' | translate) }}
      </span>
    }
  `,
  host: { class: 'app-expiry-status-column' }
})
export class ExpiryStatusColumnComponent {
  status: ExpiryStatus | null = null;
  expiryDate: string | null = null;

  @Input()
  set context(value: any) {
    const expiry = value?.row?.node?.entry?.properties?.['cm:to'];
    this.status = expiry ? ExpiryService.classify(expiry) : null;
    this.expiryDate = expiry || null;
  }
}
